import axios from "axios";

import { getNotificationAuthHeaders } from "./notificationAuthHeaders";
import {
  buildNotificationApiUrl,
  isNotificationResponseSuccessful,
  resolveNotificationErrorMessage,
} from "./notificationServiceUtils";

const ENDPOINTS = Object.freeze({
  deviceToken: "/api/notifications/device-token",
  scenarios: "/api/notifications/scenarios",
  settingsList: "/api/notifications/settings/list",
  settingsUpdate: "/api/notifications/settings/update",
  logs: "/api/notifications/logs",
});

const APP_TYPE = "admin";
const PLATFORM = "web";

const unwrapPayload = (response = {}) => {
  if (!response || typeof response !== "object") return {};
  if (
    response.body &&
    typeof response.body === "object" &&
    !Array.isArray(response.body)
  ) {
    return response.body;
  }
  return response;
};

const request = async ({
  method = "GET",
  path = "",
  params = {},
  data = {},
} = {}) => {
  const normalizedMethod = `${method || "GET"}`.trim().toUpperCase();
  const url = buildNotificationApiUrl(path);

  try {
    const response = await axios.request({
      method: normalizedMethod,
      url,
      params: normalizedMethod === "GET" ? params : undefined,
      data: normalizedMethod === "GET" ? undefined : data,
      headers: getNotificationAuthHeaders(),
    });
    const body = response?.data || {};
    if (!isNotificationResponseSuccessful(body)) {
      throw new Error(resolveNotificationErrorMessage(body));
    }
    return unwrapPayload(body);
  } catch (error) {
    const body = error?.response?.data || {};
    throw new Error(resolveNotificationErrorMessage(body));
  }
};

const registerDeviceToken = async (token = "") => {
  const normalizedToken = `${token || ""}`.trim();
  if (!normalizedToken) {
    throw new Error("Device token is required");
  }
  return request({
    method: "POST",
    path: ENDPOINTS.deviceToken,
    data: {
      token: normalizedToken,
      platform: PLATFORM,
      app: APP_TYPE,
    },
  });
};

const unregisterDeviceToken = async (token = "") => {
  const normalizedToken = `${token || ""}`.trim();
  if (!normalizedToken) {
    throw new Error("Device token is required");
  }
  return request({
    method: "DELETE",
    path: ENDPOINTS.deviceToken,
    data: {
      token: normalizedToken,
    },
  });
};

const listScenarios = async () => {
  const payload = await request({
    method: "GET",
    path: ENDPOINTS.scenarios,
  });
  return {
    ...payload,
    arrList: Array.isArray(payload?.arrList) ? payload.arrList : [],
  };
};

const listSettings = async (params = {}) => {
  const payload = {
    page: Number(params?.page) || 1,
    pageSize: Number(params?.pageSize) || 200,
    ...(params?.audienceType ? { audienceType: params.audienceType } : {}),
    ...(Array.isArray(params?.arrScenarioKeys) && params.arrScenarioKeys.length
      ? { arrScenarioKeys: params.arrScenarioKeys }
      : {}),
  };

  const postPayload = await request({
    method: "POST",
    path: ENDPOINTS.settingsList,
    data: payload,
  });
  return {
    ...postPayload,
    arrList: Array.isArray(postPayload?.arrList) ? postPayload.arrList : [],
    pagination: postPayload?.pagination || {},
  };
};

const updateSetting = async (payload = {}) => {
  const normalizedPayload = {
    ...payload,
    ...(Array.isArray(payload?.managerRecipients)
      ? { alsoNotifyRecipients: payload.managerRecipients }
      : {}),
  };
  delete normalizedPayload.managerRecipients;

  return request({
    method: "POST",
    path: ENDPOINTS.settingsUpdate,
    data: normalizedPayload,
  });
};

const getLogs = async (params = {}) => {
  const payload = await request({
    method: "GET",
    path: ENDPOINTS.logs,
    params: {
      page: Number(params?.page) || 1,
      pageSize: Number(params?.pageSize) || 20,
      ...(params?.scenarioKey ? { scenarioKey: params.scenarioKey } : {}),
      ...(params?.channel ? { channel: params.channel } : {}),
      ...(params?.status ? { status: params.status } : {}),
      ...(params?.dateRange ? { dateRange: params.dateRange } : {}),
      ...(params?.recipientId ? { recipientId: params.recipientId } : {}),
    },
  });

  const arrList = Array.isArray(payload?.arrList)
    ? payload.arrList
    : Array.isArray(payload?.items)
      ? payload.items
      : [];
  return {
    ...payload,
    arrList,
    pagination: payload?.pagination || {},
  };
};

export {
  registerDeviceToken,
  unregisterDeviceToken,
  listScenarios,
  listSettings,
  updateSetting,
  getLogs,
};
